import React, { Component, useRef, useState, useEffect } from 'react';
import GameContainer from './GameContainer';
import * as utils from '../common/Utils.js'

import { collection, query, where, getDoc, getDocs, setDoc, updateDoc, doc, Timestamp } from "firebase/firestore";
import { db } from '../firebase';
import { getPlayerByID } from '../common/Utils.js';

const DATABASE_SOURCE = utils.getDatabaseSource();

// const playoffRoundTagNames = ['play-in', 'firstround', 'quarterfinals', 'semifinals', 'championship']
const playoffRoundNames = {
  'play-in': 'Play-In',
  'first-round': 'First Round',
  'firstround': 'First Round',
  'quarterfinals': 'Quarterfinals',
  'conference-semifinal': 'Conference Semifinal',
  'semifinals': 'Semifinals',
  'conference-final': 'Conference Final',
  'championship': 'Championship',
  'final': 'Final'
}

// Used for registering swipe actions:
let startOfSwipeX = 0;
let endOfSwipeX = 0;
let startOfSwipeY = 0;
let endOfSwipeY = 0;

const getOrderedPlayoffRounds = (games) => {
  // Two label sets for the same bracket positions: seasons with real
  // conferences use 'conference-semifinal'/'conference-final', while seasons
  // without conferences (1 & 2, before conferences existed) use the plain
  // 'quarterfinals'/'semifinals'/'championship' tags they were originally
  // saved with. Each pair sits at the same relative position here so
  // sorting works correctly regardless of which set a given season uses —
  // no per-season config needed since a season only ever uses one set.
  const playoffRoundsOrder = [
    'play-in',
    'first-round', 'firstround',
    'conference-semifinal', 'quarterfinals',
    'conference-final', 'semifinals',
    'final', 'championship'
  ];

  // Collect all tags from games that are in the playoffRoundsOrder
  const tags = games.flatMap(game => {
    if (!game.playoffTags) return [];
    const tagsArray = Array.isArray(game.playoffTags) ? game.playoffTags : [game.playoffTags];
    return tagsArray.filter(tag => playoffRoundsOrder.includes(tag));
  });

  // Remove duplicates
  const uniqueTags = Array.from(new Set(tags));

  // Sort based on the predefined order
  uniqueTags.sort((a, b) => playoffRoundsOrder.indexOf(a) - playoffRoundsOrder.indexOf(b));

  return uniqueTags;
};


const Bracket = (props) => {
  const teams = props.teams;
  const games = props.games;
  const isCurrentSeason = props.isCurrentSeason;
  const showPlaceholders = props.showPlaceholders;
  // Desktop-only display variant used by the homepage widget: caps the
  // bracket to the page's content width and adds prev/next arrow buttons.
  // Has no effect below the mobile breakpoint, where the responsive
  // single-column/animated-height layout (plain CSS + updateActiveRoundHeight
  // below) applies everywhere Bracket is used, admin included.
  const compact = props.compact || false;
  // Modernized square-cornered round-pill buttons and conference labels.
  // Independent of `compact` so pages that want the full (non-narrow,
  // non-arrowed) bracket layout, like the seasons page, can still opt into
  // the modern button/label styling without the homepage's compact viewport.
  // `compact` implies this too, since the homepage widget wants both.
  const squareButtons = props.squareButtons || compact;
  const playoffGames = games.filter((game) => game.type =='playoff');

  const playoffRoundTagNames = getOrderedPlayoffRounds(games)

  // Measures the currently-active round's natural content height and
  // exposes it as a CSS custom property on the bracket container. Only
  // consumed by the mobile (<=740px) stylesheet, where it drives an
  // animated height on .league-bracket-container so the container shrinks
  // to fit just the active round instead of always reserving the tallest
  // round's height. No visual effect above that breakpoint.
  function updateActiveRoundHeight (roundTagName) {
    // Measure the outer round wrapper, not the inner round-games div directly:
    // offsetHeight excludes margins, and round-games carries its own
    // margin-top/bottom (for desktop round alignment, reset to 10px on
    // mobile) that would otherwise be missed, clipping the bottom of the
    // active round's content.
    const activeRoundEl = document.getElementById(roundTagName + '-round');
    const bracketContainerEl = document.getElementsByClassName('league-bracket-container')[0];
    if (activeRoundEl && bracketContainerEl) {
      // +4px buffer absorbs subpixel rounding from offsetHeight so content
      // never sits flush against the clipped edge.
      bracketContainerEl.style.setProperty('--active-round-height', (activeRoundEl.offsetHeight + 4) + 'px');
    }
  }

  // Centers the given round within whatever the bracket's actual visible
  // viewport is, by measuring real DOM geometry rather than relying on
  // hardcoded pixel transforms. The old round_N_of_M CSS classes assumed one
  // specific page layout and silently broke (rounds rendering fully outside
  // the visible area) whenever Bracket was mounted in a different container
  // context, like the homepage's width-capped compact viewport. This is
  // context-independent: it works the same whether the visible boundary is
  // .league-bracket-viewport (compact) or .league-bracket-container itself
  // (admin), and at any viewport width.
  function centerActiveRound (roundTagName) {
    const bracketEl = document.getElementsByClassName('league-bracket')[0];
    const roundEl = document.getElementById(roundTagName + '-round');
    const viewportEl = document.getElementsByClassName('league-bracket-viewport')[0] || document.getElementsByClassName('league-bracket-container')[0];
    if (!bracketEl || !roundEl || !viewportEl) return;

    // Reset transform first so the round's position reflects normal layout,
    // not whatever transform was previously applied.
    bracketEl.style.transform = 'translateX(0px)';

    const bracketRect = bracketEl.getBoundingClientRect();
    const roundRect = roundEl.getBoundingClientRect();
    const viewportRect = viewportEl.getBoundingClientRect();

    const roundOffsetWithinBracket = roundRect.left - bracketRect.left;
    const desiredRoundLeftWithinViewport = (viewportRect.width - roundRect.width) / 2;
    const translateX = desiredRoundLeftWithinViewport - roundOffsetWithinBracket;

    bracketEl.style.transform = `translateX(${translateX}px)`;
  }

  function getActiveRoundTagName () {
    const activeButton = document.querySelector('.league-bracket-round-button .btn-dark, .league-bracket-round-button .league-bracket-round-pill-active');
    return activeButton ? activeButton.id.replace('Button', '') : null;
  }

  useEffect(() => {
    const activeRoundTagName = getActiveRoundTagName();
    if (activeRoundTagName) {
      updateActiveRoundHeight(activeRoundTagName);
      centerActiveRound(activeRoundTagName);
    }

    // Custom web fonts (nyt-cheltenham, etc.) often aren't loaded yet on
    // first paint, so the initial measurement above can be based on
    // fallback-font metrics that are slightly shorter than the real font.
    // Re-measure once fonts are actually ready to correct for that.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        const currentRoundTagName = getActiveRoundTagName();
        if (currentRoundTagName) {
          updateActiveRoundHeight(currentRoundTagName);
        }
      });
    }
  }, [playoffGames.length]); // eslint-disable-line react-hooks/exhaustive-deps

  function displayPlayoffGame (game, showSeeds=true) {
    return (
      <GameContainer games={playoffGames} teams={teams} gameID={game.id} showPlaceholders={showPlaceholders} showSeeds={showSeeds}></GameContainer>
    )
  }

  // Renders a real game hidden via CSS, so an empty slot reserves the same
  // height as a real game without having to hand-duplicate GameContainer's
  // internal markup. `key` is passed separately since the same template
  // game may be reused to size multiple empty slots.
  function displayPlayoffGamePlaceholder (game, key) {
    return (
      <div className='league-game-container-placeholder' aria-hidden="true" key={'placeholder-' + key}>
        { displayPlayoffGame(game) }
      </div>
    )
  }

  // The play-in round can have a different number of games per conference
  // (e.g. a 9-team conference has one 8v9 play-in game, an 8-team conference
  // has none). Rather than laying out whatever games exist and centering or
  // margin-hacking the result, always render the fixed 4 slots a conference's
  // bracket can have play-in games for (matching PLAYOFF_FIRST_ROUND_MATCHUPS),
  // and place each real game in the exact slot it feeds into. Unused slots
  // become hidden placeholders sized like a real game. Since this produces
  // the same slot count/height as the first round's own 4-game column, the
  // two rounds line up row-for-row using the existing flex layout with no
  // extra CSS needed.
  function buildPlayInConferenceSlots (conferenceGames, templateGame) {
    const slots = new Array(utils.PLAYOFF_FIRST_ROUND_MATCHUPS.length).fill(null);

    conferenceGames.forEach((game) => {
      const slotIndex = utils.getPlayInSlotIndex(game);
      if (slotIndex !== -1) {
        slots[slotIndex] = game;
      }
    });

    return slots.map((game, index) =>
      game ? displayPlayoffGame(game) : displayPlayoffGamePlaceholder(templateGame, index)
    );
  }

  // Finds the game (in any round) whose playoffTags include 'winner_<sourceGameID>'
  // — i.e. the game sourceGame's winner advances into.
  function findWinnerDestinationGame (sourceGameID) {
    return playoffGames.find(g => (g.playoffTags || []).includes(`winner_${sourceGameID}`));
  }

  // Aligns a round's games (e.g. play-in) to the exact row of the downstream
  // game each one's winner advances into, using the winner_<id> tags already
  // used for auto-advancing teams (see AdminGames.js). This works regardless
  // of bracket shape/seed structure — unlike the conference slot system
  // below, which assumes the standard 8-seed layout — since it's derived
  // directly from each game's actual destination rather than a seed table.
  // Falls back to a plain ordered list if no winner_<id> links are found.
  function buildWinnerAlignedSlots (roundGames) {
    const entries = roundGames.map(game => {
      const destGame = findWinnerDestinationGame(game.id);
      if (!destGame) return { game, slotIndex: null, destRoundGameCount: 0 };

      const destRoundTag = (destGame.playoffTags || []).find(t => playoffRoundTagNames.includes(t));
      const destRoundGames = playoffGames
        .filter(g => (g.playoffTags || []).includes(destRoundTag))
        .sort((a, b) => (a.playoffRoundOrder || 0) - (b.playoffRoundOrder || 0));

      const slotIndex = destRoundGames.findIndex(g => g.id === destGame.id);
      return { game, slotIndex: slotIndex === -1 ? null : slotIndex, destRoundGameCount: destRoundGames.length };
    });

    // Slot count must match the full destination round's game count, not
    // just the highest referenced index — otherwise a destination round
    // whose last matchup(s) never needed a play-in feed (e.g. this round's
    // referenced games only land at indices 0 and 2 of 4) would undercount
    // and misalign everything, since space-evenly distributes based on how
    // many slots actually exist.
    const slotCount = Math.max(0, ...entries.map(e => e.destRoundGameCount));

    if (slotCount === 0) {
      return [...roundGames]
        .sort((a, b) => (a.playoffRoundOrder || 0) - (b.playoffRoundOrder || 0))
        .map(game => displayPlayoffGame(game));
    }

    const slots = new Array(slotCount).fill(null);
    entries.forEach(({ game, slotIndex }) => {
      if (slotIndex !== null) slots[slotIndex] = game;
    });

    const templateGame = roundGames[0];
    return slots.map((game, index) =>
      game ? displayPlayoffGame(game) : displayPlayoffGamePlaceholder(templateGame, index)
    );
  }

  function displayPlayInGames (games) {
    let easternGames = games.filter((game) => game.playoffTags.includes('east'))
    let westernGames = games.filter((game) => game.playoffTags.includes('west'))

    // Seasons without conferences (e.g. 1 & 2) have no 'east'/'west' tags on
    // any game, so the slot-based conference layout below has nothing to
    // build off of (and would try to size placeholders off an undefined
    // template game). Align using winner_<id> tags instead.
    if (easternGames.length === 0 && westernGames.length === 0) {
      return (
        <div className='game-with-label'>
          <div className='conference-games'>
            { buildWinnerAlignedSlots(games) }
          </div>
        </div>
      );
    }

    let templateGame = easternGames[0] || westernGames[0];

    // When a conference has no real games this round (e.g. play-in), its
    // label is hidden (not removed) rather than left out entirely: leaving
    // it out would shrink that block by exactly one line of text, throwing
    // off the vertical alignment between the two stacked conference blocks
    // on bigger screens. Mobile still collapses the whole block via
    // .conference-games-empty, so this placeholder never shows there.
    return (
      <div className='league-bracket-round-conferences'>
        <div className={ `conference-games-with-label${easternGames.length === 0 ? ' conference-games-empty' : ''}` }>
          { squareButtons && <h4 className={ `text-align-center${easternGames.length === 0 ? ' conference-label-placeholder' : ''}` }>Eastern Conference</h4> }
          <div className='conference-games'>
            { buildPlayInConferenceSlots(easternGames, templateGame) }
          </div>
        </div>

        <div className={ `conference-games-with-label${westernGames.length === 0 ? ' conference-games-empty' : ''}` }>
          { squareButtons && <h4 className={ `text-align-center${westernGames.length === 0 ? ' conference-label-placeholder' : ''}` }>Western Conference</h4> }
          <div className='conference-games'>
            { buildPlayInConferenceSlots(westernGames, templateGame) }
          </div>
        </div>
      </div>
    );
  }

  function displayPlayoffGames (round, games) {
    if (round === 'play-in') {
      return displayPlayInGames(games);
    }

    let sortedGames = games.sort(function(a,b) {
      let orderA = a.playoffRoundOrder || 0;
      let orderB = b.playoffRoundOrder || 0;
      if (orderA < orderB) {
        return -1;
      } else if (orderB < orderA) {
        return 1;
      } else {
        return 0;
      }
    })

    let easternGames = sortedGames.filter((game) => game.playoffTags.includes('east'))
    let westernGames = sortedGames.filter((game) => game.playoffTags.includes('west'))

    if (easternGames.length > 0 && westernGames.length > 0) {
      let easternGameElements = easternGames.map((game, place) => displayPlayoffGame(game))
      let westernGameElements = westernGames.map((game, place) => displayPlayoffGame(game))

      return (
        <div className='league-bracket-round-conferences'>
          <div className='conference-games-with-label'>
            { squareButtons && <h4 className='text-align-center'>Eastern Conference</h4> }
            <div className='conference-games'>
              { easternGameElements }
            </div>
          </div>

          <div className='conference-games-with-label'>
            { squareButtons && <h4 className='text-align-center'>Western Conference</h4> }
            <div className='conference-games'>
              { westernGameElements }
            </div>
          </div>
        </div>
      );
    } else {
      let gameElements = sortedGames.map((game, place) => displayPlayoffGame(game))

      return (
        <div className='game-with-label'>
          <div className='conference-games'>
            { gameElements }
          </div>
        </div>
      );
    }
  }

  function displayPlayoffsButtons () {
    let playoffButtons = [];

    const numberPlayoffRounds = playoffRoundTagNames.length
    let roundGames = {}

    for (let i = 0; i < playoffGames.length; i++) {
      let game = playoffGames[i]

      // playoff tags can be a string or a list currently.
      // keep this until we support only one
      if (Array.isArray(game.playoffTags)) {
        for (let j = 0; j < game.playoffTags.length; j++) {
          let tag = game.playoffTags[j]

          if (playoffRoundTagNames.includes(tag)) {
            if (tag in roundGames) {
              roundGames[tag].push(game)
            } else {
              roundGames[tag] = [game]
            }
          }
        }
      } else {
        if (playoffRoundTagNames.includes(game.playoffTags)) {
          if (game.playoffTags in roundGames) {
            roundGames[game.playoffTags].push(game)
          } else {
            roundGames[game.playoffTags] = [game]
          }
        }
      }
    }

    const numberOfRounds = Object.keys(roundGames).length
    let roundIndex = -1
    while (true) {
      if (Math.abs(roundIndex) > numberOfRounds) {
        break
      }

      let roundTag = playoffRoundTagNames.at(roundIndex)

      let gamesThisRound = roundGames[roundTag]
      let statuses = gamesThisRound.map((game) => game.status)
      let roundComplete = statuses.every(status => status === 'completed');

      if (roundComplete) {
        if (roundIndex != -1) {
          roundIndex += 1
        }
        break
      } else {
        if (Math.abs(roundIndex) == numberOfRounds) {
          break
        }
        roundIndex -= 1
      }
    }
    let currentRoundIndex = numberPlayoffRounds + roundIndex + 1

    const baseButtonClass = squareButtons ? 'league-bracket-round-pill' : 'btn';
    const activeButtonClass = squareButtons ? 'league-bracket-round-pill-active' : 'btn-dark';

    for (let i = 0; i < playoffRoundTagNames.length; i++) {
      let playoffRoundTagName = playoffRoundTagNames[i];
      let playoffRoundName = playoffRoundNames[playoffRoundTagName];

      let buttonClass = baseButtonClass;
      if (isCurrentSeason) {
        if (i == currentRoundIndex - 1) {
          buttonClass += ' ' + activeButtonClass;
        }
      } else {
        if (playoffRoundTagName == 'championship' || playoffRoundTagName == 'final') {
          buttonClass += ' ' + activeButtonClass;
        }
      }

      playoffButtons.push(
        <>
          <div className='league-bracket-round-button'>
            <a id={ playoffRoundTagName + "Button" } type="button" className={ buttonClass } onClick={ () => { clickPlayoffRoundButton(playoffRoundTagName) } }>{ playoffRoundName }</a>
          </div>
        </>
      )
    }

    return (
      <div className='league-bracket-round-buttons'>
        { playoffButtons }
      </div>
    )
  }

  function clickPlayoffRoundButton(playoffRoundTagName){
    const activeButtonClass = squareButtons ? 'league-bracket-round-pill-active' : 'btn-dark';

    centerActiveRound(playoffRoundTagName);

    let button = document.getElementById(playoffRoundTagName + 'Button');
    button.classList.add(activeButtonClass)

    for (let j = 0; j < playoffRoundTagNames.length; j++) {
      let playoffOtherRoundTagName = playoffRoundTagNames[j];
      if (playoffRoundTagName == playoffOtherRoundTagName) {
        continue;
      }

      let playoffRoundButton = document.getElementById(playoffOtherRoundTagName + 'Button');
      playoffRoundButton.classList.remove(activeButtonClass)
    }

    updateActiveRoundHeight(playoffRoundTagName);
  }

  function displayPlayoffs () {
    let roundGames = {}

    for (let i = 0; i < playoffGames.length; i++) {
      let game = playoffGames[i]

      // playoff tags can be a string or a list currently.
      // keep this until we support only one
      if (Array.isArray(game.playoffTags)) {
        for (let j = 0; j < game.playoffTags.length; j++) {
          let tag = game.playoffTags[j]

          if (playoffRoundTagNames.includes(tag)) {
            if (tag in roundGames) {
              roundGames[tag].push(game)
            } else {
              roundGames[tag] = [game]
            }
          }
        }
      } else {
        if (playoffRoundTagNames.includes(game.playoffTags)) {
          if (game.playoffTags in roundGames) {
            roundGames[game.playoffTags].push(game)
          } else {
            roundGames[game.playoffTags] = [game]
          }
        }
      }
    }

    const numberOfRounds = Object.keys(roundGames).length

    return (
      <div className="league-bracket" onTouchStart={(e) => {
        startOfSwipeX = e.changedTouches[0].screenX;
        startOfSwipeY = e.changedTouches[0].screenY;
      }} onTouchEnd={(e) => {
        endOfSwipeX = e.changedTouches[0].screenX;
        endOfSwipeY = e.changedTouches[0].screenY;
        checkDirectionOfSwipe(numberOfRounds)
      }}>
        { displayPlayoffsForRounds(roundGames) }
      </div>
    );
  }

  function swipePlayoffRound (swipeDirection, rounds) {
    const activeButtonClass = squareButtons ? 'league-bracket-round-pill-active' : 'btn-dark';

    let playinRoundButton = document.getElementById('play-inButton');
    let firstRoundButton = document.getElementById('first-roundButton');
    let conferenceSemifinalButton = document.getElementById('conference-semifinalButton');
    let conferenceFinalButton = document.getElementById('conference-finalButton');
    let finalButton = document.getElementById('finalButton');

    let roundButtons = [playinRoundButton, firstRoundButton, conferenceSemifinalButton, conferenceFinalButton, finalButton].filter(Boolean);

    for (let i = 0; i < roundButtons.length; i++) {
      let roundButton = roundButtons[i]
      if (roundButton.classList.contains(activeButtonClass)){
        if (swipeDirection == 'right' && i >= roundButtons.length - 1) {
        } else if (swipeDirection == 'left' && i <= roundButtons.length - rounds) {
        } else if (swipeDirection == 'left') {
          roundButtons[i-1].scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
          roundButtons[i-1].click();
        } else if (swipeDirection == 'right') {
          roundButtons[i+1].scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
          roundButtons[i+1].click();
        }
        break;
      }
    }
  }

  function checkDirectionOfSwipe (rounds) {
    if (Math.abs(startOfSwipeX - endOfSwipeX) < 10) {
      return;
    }
    if (Math.abs(startOfSwipeX - endOfSwipeX) < Math.abs(startOfSwipeY - endOfSwipeY)) {
      return;
    }

    if (endOfSwipeX < startOfSwipeX) {
      swipePlayoffRound('right', rounds)
    } else if (endOfSwipeX > startOfSwipeX) {
      swipePlayoffRound('left', rounds)
    }
  }

  function displayPlayoffsForRounds(roundGames) {
    // roundGames is built by iterating playoffGames in whatever order
    // Firestore happened to return documents in, which is not guaranteed to
    // match round order (play-in, first-round, ...). The round_N_of_M
    // transform classes assume rounds render left-to-right in that exact
    // canonical order, so the DOM order here must be explicitly sorted to
    // match playoffRoundTagNames rather than relying on object key
    // insertion order.
    const playoffBracket = Object.entries(roundGames)
      .sort(([roundA], [roundB]) => playoffRoundTagNames.indexOf(roundA) - playoffRoundTagNames.indexOf(roundB))
      .map(([round, games]) => {
        return displayPlayoffRound(round, games)
      })

    return (
      <>
        { playoffBracket }
      </>
    );
  }

  function displayPlayoffRound(round, games) {
    let roundNumber = playoffRoundTagNames.indexOf(round) + 1

    // The play-in round always renders the fixed 4-slots-per-conference
    // structure (see displayPlayInGames), so it should always get the same
    // round-level margin treatment as the first round's real 8 games
    // (4 per conference), regardless of how many play-in games actually
    // exist this season.
    let roundGamesCount = round === 'play-in' ? utils.PLAYOFF_FIRST_ROUND_MATCHUPS.length * 2 : games.length;

    return (
      <div id={ round + '-round' } className={ `league-bracket-round round-${roundNumber}` }>
        <div className={ `league-bracket-round-games round-games-${roundGamesCount}` }>
          { displayPlayoffGames(round, games) }
        </div>
      </div>
    )
  }

  if (playoffGames.length > 0) {
    return (
      <div className={ compact ? '' : 'league-body' }>
        <p className="league-title-standard-secondary text-align-center">Playoff Bracket</p>
          { displayPlayoffsButtons() }
        { compact ? (
          <div className="league-bracket-container-compact">
            <button
              type="button"
              className="league-bracket-nav-arrow league-bracket-nav-arrow-left"
              aria-label="Previous round"
              onClick={ () => swipePlayoffRound('left', playoffRoundTagNames.length) }
            >‹</button>
            <div className="league-bracket-viewport">
              <div className="league-bracket-container">
                { displayPlayoffs() }
              </div>
            </div>
            <button
              type="button"
              className="league-bracket-nav-arrow league-bracket-nav-arrow-right"
              aria-label="Next round"
              onClick={ () => swipePlayoffRound('right', playoffRoundTagNames.length) }
            >›</button>
          </div>
        ) : (
          <div className="league-bracket-container">
            { displayPlayoffs() }
          </div>
        ) }
      </div>
    );
  } else {
    return
  }

}

export default Bracket;
